import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import SearchableSelect from '@/components/common/SearchableSelect';
import EmailTable from '@/components/global-admin/EmailTable';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { kindLabel, type SentEmailRow } from '@/lib/outbox';

type Payload = { emails: SentEmailRow[]; kinds: string[]; truncated: boolean };
type CustomerOption = { organization_id: string; name: string };

const ANY = '__any__';

export default function OutboxPage() {
  const [customer, setCustomer] = React.useState(ANY);
  const [kind, setKind] = React.useState(ANY);
  const [q, setQ] = React.useState('');
  const [debounced, setDebounced] = React.useState('');

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(q.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [q]);

  const customers = useQuery({
    queryKey: ['global-admin-customers'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/customers/')).data as { customers: CustomerOption[] };
    },
  });

  const outbox = useQuery({
    queryKey: ['global-admin-outbox', customer, kind, debounced],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const params = new URLSearchParams();
      if (customer !== ANY) params.set('organization_id', customer);
      if (kind !== ANY) params.set('kind', kind);
      if (debounced) params.set('q', debounced);
      return (await api.get(`/global-admin/outbox/?${params.toString()}`)).data as Payload;
    },
  });

  return (
    <div className="space-y-6" data-testid="outbox-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Outbox</h2>
        <p className="text-muted-foreground">
          Every email the platform sent on its own: invoices, overdue reminders, approvals, sign-up codes.
          Filter by customer to see exactly what they received and when, and whether the mail server took it.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sent emails</CardTitle>
          <CardDescription>Newest first. Click a row to read the text that went out.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <SearchableSelect
              className="w-64"
              value={customer}
              onChange={setCustomer}
              placeholder="Any customer"
              searchPlaceholder="Search customers…"
              options={[
                { value: ANY, label: 'Any customer' },
                ...(customers.data?.customers ?? []).map((c) => ({ value: c.organization_id, label: c.name, keywords: c.organization_id })),
              ]}
              testId="outbox-customer"
            />
            <SearchableSelect
              className="w-52"
              value={kind}
              onChange={setKind}
              placeholder="Any kind"
              searchPlaceholder="Search kinds…"
              options={[
                { value: ANY, label: 'Any kind' },
                ...(outbox.data?.kinds ?? []).map((k) => ({ value: k || 'other', label: kindLabel(k) })),
              ]}
              testId="outbox-kind"
            />
            <div className="relative min-w-[16rem] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipient, subject or text…" className="pl-9" data-testid="outbox-search" />
            </div>
          </div>

          {outbox.isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              <EmailTable emails={outbox.data?.emails ?? []} />
              {outbox.data?.truncated && (
                <p className="text-xs text-muted-foreground">Showing the newest 200; narrow the filters to see older mail.</p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
